<nav class="bg-chrome shadow-nav sticky top-0 z-20 w-full">
    <div class="flex h-14 items-center justify-between px-4 md:px-6">
        <div class="flex min-w-0 items-center gap-3 md:gap-4">
            <a routerLink="/" class="flex flex-none items-center">
                <span class="sr-only">ASM Store Portal</span>
                <img src="assets/theme/images/asm-logo-sm.png" alt="ASM Group" class="h-6 w-auto" />
            </a>
            @if (environment === 'DEV') {
                <span class="text-2xs flex-none rounded-full bg-amber-400/20 px-2 py-0.5 font-bold tracking-wider text-amber-300 uppercase">Dev</span>
            }

            <!-- Store context (desktop / tablet) -->
            @if (!viewport.isMobile() && locationContext.loading()) {
                <span class="h-6 w-px bg-white/20"></span>
                <div class="flex min-w-0 items-center gap-2.5" role="status" aria-label="Loading store context">
                    <stm-skeleton width="1.75rem" height="1.75rem" [circle]="true" />
                    <div class="min-w-0 flex flex-col gap-1.5" aria-hidden="true">
                        <stm-skeleton width="8rem" height="0.75rem" />
                        <stm-skeleton width="12rem" height="0.625rem" />
                    </div>
                </div>
            } @else if (!viewport.isMobile() && locationContext.location(); as loc) {
                <span class="h-6 w-px bg-white/20"></span>
                <div class="flex min-w-0 items-center gap-2.5">
                    <span class="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/15 text-white">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" aria-hidden="true"><path [attr.d]="icons.store" /></svg>
                    </span>
                    <div class="min-w-0 leading-tight">
                        <div class="truncate text-sm font-bold text-white">
                            @if (loc.usesNumber) {
                                {{ 'Store' | transloco }} {{ loc.idx }}
                                @if (loc.brandName) {
                                    <span class="font-normal text-blue-200"> · {{ loc.brandName }}</span>
                                }
                            } @else {
                                {{ loc.name }}
                            }
                        </div>
                        <div class="text-2xs truncate text-blue-100">
                            @if (loc.street) {
                                {{ loc.street }}, {{ loc.city }}
                            }
                            @if (loc.primaryPhone) {
                                · {{ loc.primaryPhone | formatPhone }}
                            }
                            @if (loc.primaryContact) {
                                · {{ loc.primaryContact }}
                            }
                        </div>
                    </div>
                </div>
            }
        </div>

        @if (authService.isAuthenticated$ | async) {
            <div id="menu-wrapper" class="relative flex-none">
                <button type="button" (click)="showMenu.update((value) => !value)" id="nav-side-menu-icon" class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg bg-white/10 text-white hover:bg-white/20">
                    <span class="sr-only">Open menu</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="pointer-events-none h-5 w-5" aria-hidden="true">
                        <path [attr.d]="icons.cog" />
                        <circle cx="12" cy="12" r="3" />
                    </svg>
                </button>
                @if (showMenu()) {
                    <div id="nav-side-menu" role="menu" tabindex="0" class="divide-y divide-gray-100">
                        <p class="truncate px-4 py-3.5" role="none">
                            <span class="block text-sm text-gray-500" role="none">{{ 'NavMenu.SignedInAs' | transloco }}</span>
                            <span class="mt-0.5 text-base font-semibold" role="none"> {{ userInfo()?.Email }}</span>
                        </p>
                        <div class="py-1.5" role="none">
                            <button (click)="goToProfile()" type="button" class="nav-menu-item" role="menuitem" tabindex="-1">{{ 'NavMenu.Profile' | transloco }}</button>
                            <button (click)="getReleaseNotes()" type="button" class="nav-menu-item" role="menuitem" tabindex="-1">{{ 'NavMenu.ReleaseNotes' | transloco }}</button>
                        </div>
                        <div class="py-1.5" role="none">
                            <button (click)="onLogout()" type="button" class="nav-menu-item" role="menuitem" tabindex="-1">{{ 'NavMenu.Signout' | transloco }}</button>
                        </div>
                    </div>
                }
            </div>
        }
    </div>

    <!-- Store context (mobile) -->
    @if (viewport.isMobile() && locationContext.loading()) {
        <div class="flex items-center gap-2.5 px-4 pt-2 pb-3" role="status" aria-label="Loading store context">
            <stm-skeleton width="2rem" height="2rem" [circle]="true" />
            <div class="min-w-0 flex-1 leading-tight" aria-hidden="true">
                <div class="mb-1.5"><stm-skeleton width="min(10rem, 55vw)" height="1rem" /></div>
                <stm-skeleton width="min(14rem, 72vw)" height="0.75rem" />
            </div>
        </div>
    } @else if (viewport.isMobile() && locationContext.location(); as loc) {
        <div class="flex items-center gap-2.5 px-4 pt-0.5 pb-3">
            <span class="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-white/15 text-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4" aria-hidden="true"><path [attr.d]="icons.store" /></svg>
            </span>
            <div class="min-w-0 leading-tight">
                <div class="font-display truncate text-base font-bold text-white">
                    @if (loc.usesNumber) {
                        {{ 'Store' | transloco }} {{ loc.idx }}
                        @if (loc.brandName) {
                            <span class="font-normal text-blue-200"> · {{ loc.brandName }}</span>
                        }
                    } @else {
                        {{ loc.name }}
                    }
                </div>
                <div class="truncate text-xs text-blue-100">
                    @if (loc.street) {
                        {{ loc.street }}
                    }
                    @if (loc.primaryPhone) {
                        · {{ loc.primaryPhone | formatPhone }}
                    }
                </div>
            </div>
        </div>
    }
</nav>
